import { supabase } from "@/integrations/supabase/client";

// Minimal device fingerprint for fraud prevention only.
// Canvas fingerprinting and granular browser entropy have been intentionally
// removed for RGPD data minimization. We keep a stable random device_id
// generated locally and a low-entropy hash (UA family + language + timezone)
// that does not uniquely identify the browser on its own.
const generateFingerprint = (): string => {
  const data = [
    navigator.language || "",
    Intl.DateTimeFormat().resolvedOptions().timeZone || "",
  ].join("|");

  let hash = 0;
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
};

// Get or create device ID
export const getDeviceId = (): string => {
  const storageKey = 'player_device_id';
  
  // Try localStorage first
  let deviceId = localStorage.getItem(storageKey);
  
  if (!deviceId) {
    // Generate new UUID
    deviceId = crypto.randomUUID();
    localStorage.setItem(storageKey, deviceId);
  }
  
  return deviceId;
};

// Hash IP (simulated client-side - in production this should be done server-side)
const getIpHash = async (): Promise<string | null> => {
  try {
    // In a real implementation, this would be done server-side
    // For now, we'll use a placeholder based on timezone + language as a weak proxy
    const proxy = `${Intl.DateTimeFormat().resolvedOptions().timeZone}_${navigator.language}`;
    let hash = 0;
    for (let i = 0; i < proxy.length; i++) {
      hash = ((hash << 5) - hash) + proxy.charCodeAt(i);
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  } catch {
    return null;
  }
};

export interface PlayerIdentity {
  id: string;
  device_id: string;
  fp_hash: string | null;
  ip_hash: string | null;
  created_at: string;
  last_seen_at: string;
  is_tester?: boolean;
}

// Resolve or create player identity (multi-tenant: requires clientId)
// Writes go through the resolve-player-identity edge function (service role)
// so that RLS on player_identities can stay locked down.
export const resolvePlayerIdentity = async (clientId: string): Promise<PlayerIdentity | null> => {
  if (!clientId) {
    console.error('[resolvePlayerIdentity] clientId is required for multi-tenant isolation');
    return null;
  }

  try {
    const deviceId = getDeviceId();
    const fpHash = generateFingerprint();
    const ipHash = await getIpHash();

    const { data, error } = await supabase.functions.invoke('resolve-player-identity', {
      body: { action: 'resolve', clientId, deviceId, fpHash, ipHash },
    });

    if (error) {
      console.error('[resolvePlayerIdentity] invoke error:', error);
      return null;
    }

    return (data?.identity as PlayerIdentity) ?? null;
  } catch (err) {
    console.error('[resolvePlayerIdentity] Error:', err);
    return null;
  }
};

// Get blocked actions for a player (filtered by client for multi-tenant isolation)
export const getBlockedActions = async (identityId: string, clientId: string): Promise<string[]> => {
  try {
    const { data, error } = await supabase.functions.invoke('resolve-player-identity', {
      body: { action: 'get-blocked-actions', identityId, clientId },
    });

    if (error) {
      console.error('Error fetching blocked actions:', error);
      return [];
    }

    return (data?.blockedActions as string[]) ?? [];
  } catch (err) {
    console.error('Error in getBlockedActions:', err);
    return [];
  }
};


// Block an action for a player (permanent, status "done").
// Goes through the resolve-player-identity edge function: players can no longer
// write player_action_blocks directly since RLS was locked down on 2026-06-01.
export const blockAction = async (
  identityId: string,
  action: string,
  clientId: string
): Promise<boolean> => {
  try {
    const { error } = await supabase.functions.invoke('resolve-player-identity', {
      body: { action: 'block-action', identityId, clientId, deviceId: getDeviceId(), blockedAction: action },
    });

    if (error) {
      console.error('Error blocking action:', error);
      return false;
    }
    
    return true;
  } catch (err) {
    console.error('Error in blockAction:', err);
    return false;
  }
};

// Set tester status for a player
export const setTesterStatus = async (
  identityId: string,
  isTester: boolean
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('player_identities')
      .update({ is_tester: isTester })
      .eq('id', identityId);
    
    if (error) {
      console.error('Error setting tester status:', error);
      return false;
    }
    
    return true;
  } catch (err) {
    console.error('Error in setTesterStatus:', err);
    return false;
  }
};
